import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  LivePreviewState,
  LivePreviewActions,
  PlayState,
  PreviewMode,
  InputState,
  Touch,
  RecordingState,
  DEFAULT_VIEWPORT_SETTINGS,
  DEFAULT_PLAY_MODE_SETTINGS,
  DEFAULT_DEBUG_OVERLAY,
  DEFAULT_DEBUG_RENDER,
  DEFAULT_PERFORMANCE_STATS,
  DEFAULT_SCREENSHOT_SETTINGS,
  DEFAULT_RECORDING_SETTINGS,
} from '../../core/types/live-preview.types';
import { getPresetById } from '../utils/device-presets';

const generateId = () => Math.random().toString(36).substring(2, 11);

interface LivePreviewStore extends LivePreviewState, LivePreviewActions {}

const initialInputState: InputState = {
  keys: new Set(),
  mousePosition: { x: 0, y: 0 },
  mouseButtons: new Set(),
  touches: [],
  gamepadConnected: false,
  gamepadState: null,
};

const initialRecordingState: RecordingState = {
  isRecording: false,
  isPaused: false,
  duration: 0,
  frames: 0,
  size: 0,
};

export const useLivePreviewStore = create<LivePreviewStore>()(
  immer((set, get) => ({
      // Initial state
      viewport: { ...DEFAULT_VIEWPORT_SETTINGS },
      previewMode: 'canvas' as PreviewMode,
      playState: 'stopped' as PlayState,
      playModeSettings: { ...DEFAULT_PLAY_MODE_SETTINGS },
      sceneSnapshot: null,
      debugOverlay: { ...DEFAULT_DEBUG_OVERLAY },
      debugRender: { ...DEFAULT_DEBUG_RENDER },
      stats: { ...DEFAULT_PERFORMANCE_STATS },
      inputState: initialInputState,
      inputSimulationEnabled: false,
      touchSimulationEnabled: false,
      screenshotSettings: { ...DEFAULT_SCREENSHOT_SETTINGS },
      recordingSettings: { ...DEFAULT_RECORDING_SETTINGS },
      recordingState: { ...initialRecordingState },
      currentFrame: 0,
      targetFps: 60,
      actualFps: 0,
      deltaTime: 0,

      // Preview mode
      setPreviewMode: (mode) => {
        set((state) => {
          state.previewMode = mode;
        });
      },

      // Viewport actions
      setDevicePreset: (presetId) => {
        set((state) => {
          const preset = presetId ? getPresetById(presetId) : null;
          state.viewport.devicePresetId = presetId;
          if (preset) {
            state.viewport.customWidth = preset.width;
            state.viewport.customHeight = preset.height;
            state.viewport.isPortrait = preset.isPortrait;
          }
        });
      },

      setCustomResolution: (width, height) => {
        set((state) => {
          state.viewport.devicePresetId = null;
          state.viewport.customWidth = width;
          state.viewport.customHeight = height;
        });
      },

      toggleOrientation: () => {
        set((state) => {
          state.viewport.isPortrait = !state.viewport.isPortrait;
          // Swap width and height
          const temp = state.viewport.customWidth;
          state.viewport.customWidth = state.viewport.customHeight;
          state.viewport.customHeight = temp;
        });
      },

      setScale: (scale) => {
        set((state) => {
          state.viewport.scale = Math.max(0.25, Math.min(2, scale));
        });
      },

      setFitToPanel: (fit) => {
        set((state) => {
          state.viewport.fitToPanel = fit;
        });
      },

      toggleAspectRatioLock: () => {
        set((state) => {
          state.viewport.aspectRatioLocked = !state.viewport.aspectRatioLocked;
        });
      },

      // Play mode actions
      play: () => {
        set((state) => {
          if (state.playState === 'stopped') {
            // Create scene snapshot for restoration later
            state.sceneSnapshot = {
              id: generateId(),
              timestamp: Date.now(),
              gameObjects: {},
              rootIds: [],
            };
          }
          state.playState = 'playing';
          state.currentFrame = 0;
        });
      },

      pause: () => {
        set((state) => {
          if (state.playState === 'playing') {
            state.playState = 'paused';
          }
        });
      },

      stop: () => {
        set((state) => {
          state.playState = 'stopped';
          state.currentFrame = 0;
          state.sceneSnapshot = null;
        });
      },

      step: () => {
        const { playState } = get();
        if (playState === 'paused') {
          set((state) => {
            state.currentFrame += 1;
          });
        }
      },

      updatePlayModeSettings: (settings) => {
        set((state) => {
          Object.assign(state.playModeSettings, settings);
        });
      },

      // Debug actions
      toggleDebugOverlay: () => {
        set((state) => {
          state.debugOverlay.enabled = !state.debugOverlay.enabled;
        });
      },

      updateDebugOverlay: (settings) => {
        set((state) => {
          Object.assign(state.debugOverlay, settings);
        });
      },

      updateDebugRender: (settings) => {
        set((state) => {
          Object.assign(state.debugRender, settings);
        });
      },

      updateStats: (stats) => {
        set((state) => {
          Object.assign(state.stats, stats);
        });
      },

      // Input actions
      setInputState: (inputState) => {
        set((state) => {
          if (inputState.keys) state.inputState.keys = inputState.keys;
          if (inputState.mousePosition) state.inputState.mousePosition = inputState.mousePosition;
          if (inputState.mouseButtons) state.inputState.mouseButtons = inputState.mouseButtons;
          if (inputState.touches) state.inputState.touches = inputState.touches;
          if (inputState.gamepadConnected !== undefined) {
            state.inputState.gamepadConnected = inputState.gamepadConnected;
          }
          if (inputState.gamepadState) state.inputState.gamepadState = inputState.gamepadState;
        });
      },

      toggleInputSimulation: () => {
        set((state) => {
          state.inputSimulationEnabled = !state.inputSimulationEnabled;
        });
      },

      toggleTouchSimulation: () => {
        set((state) => {
          state.touchSimulationEnabled = !state.touchSimulationEnabled;
        });
      },

      simulateKeyPress: (key) => {
        set((state) => {
          const newKeys = new Set(state.inputState.keys);
          if (newKeys.has(key)) {
            newKeys.delete(key);
          } else {
            newKeys.add(key);
          }
          state.inputState.keys = newKeys;
        });
      },

      simulateMouseClick: (x, y, button) => {
        set((state) => {
          state.inputState.mousePosition = { x, y };
          const newButtons = new Set(state.inputState.mouseButtons);
          newButtons.add(button);
          state.inputState.mouseButtons = newButtons;
        });
        // Auto-release after short delay
        setTimeout(() => {
          set((state) => {
            const newButtons = new Set(state.inputState.mouseButtons);
            newButtons.delete(button);
            state.inputState.mouseButtons = newButtons;
          });
        }, 100);
      },

      simulateTouch: (touches: Touch[]) => {
        set((state) => {
          state.inputState.touches = touches;
        });
      },

      // Recording actions
      takeScreenshot: async () => {
        // Implementation will be handled by the component
        return '';
      },

      startRecording: () => {
        set((state) => {
          state.recordingState.isRecording = true;
          state.recordingState.isPaused = false;
          state.recordingState.duration = 0;
          state.recordingState.frames = 0;
          state.recordingState.size = 0;
        });
      },

      stopRecording: async () => {
        set((state) => {
          state.recordingState.isRecording = false;
          state.recordingState.isPaused = false;
        });
        // Return a placeholder - actual implementation in component
        return new Blob();
      },

      pauseRecording: () => {
        set((state) => {
          if (state.recordingState.isRecording) {
            state.recordingState.isPaused = true;
          }
        });
      },

      resumeRecording: () => {
        set((state) => {
          if (state.recordingState.isRecording && state.recordingState.isPaused) {
            state.recordingState.isPaused = false;
          }
        });
      },

      updateScreenshotSettings: (settings) => {
        set((state) => {
          Object.assign(state.screenshotSettings, settings);
        });
      },

      updateRecordingSettings: (settings) => {
        set((state) => {
          Object.assign(state.recordingSettings, settings);
        });
      },

      // Reset
      reset: () => {
        set((state) => {
          state.viewport = { ...DEFAULT_VIEWPORT_SETTINGS };
          state.previewMode = 'iframe';
          state.playState = 'stopped';
          state.playModeSettings = { ...DEFAULT_PLAY_MODE_SETTINGS };
          state.sceneSnapshot = null;
          state.debugOverlay = { ...DEFAULT_DEBUG_OVERLAY };
          state.debugRender = { ...DEFAULT_DEBUG_RENDER };
          state.stats = { ...DEFAULT_PERFORMANCE_STATS };
          state.inputState = initialInputState;
          state.inputSimulationEnabled = false;
          state.touchSimulationEnabled = false;
          state.recordingState = { ...initialRecordingState };
          state.currentFrame = 0;
        });
      },

      // Project state methods
      loadProjectState: (projectState: {
        devicePresetId?: string | null;
        customWidth?: number;
        customHeight?: number;
        scale?: number;
        fitToPanel?: boolean;
      }) => {
        set((state) => {
          state.viewport.devicePresetId = projectState.devicePresetId ?? null;
          state.viewport.customWidth = projectState.customWidth ?? 720;
          state.viewport.customHeight = projectState.customHeight ?? 1280;
          state.viewport.scale = projectState.scale ?? 1;
          state.viewport.fitToPanel = projectState.fitToPanel ?? true;
        });
      },

      getProjectState: () => {
        const state = get();
        return {
          devicePresetId: state.viewport?.devicePresetId ?? null,
          customWidth: state.viewport?.customWidth ?? 720,
          customHeight: state.viewport?.customHeight ?? 1280,
          scale: state.viewport?.scale ?? 1,
          fitToPanel: state.viewport?.fitToPanel ?? true,
        };
      },

      resetProjectState: () => {
        set((state) => {
          state.playState = 'stopped';
          state.viewport = { ...DEFAULT_VIEWPORT_SETTINGS };
          state.sceneSnapshot = null;
          state.currentFrame = 0;
        });
      },
    }))
);
